import { useState } from "react";
import { ServiceTemplateRecurrenceStatus } from "@common/types/serviceTemplateRecurrence";
import {
    useDeleteServiceTemplateScheduleMutation,
    useUpdateServiceTemplateScheduleStatusMutation,
} from "@api/serviceTemplateSchedule.api";
import { ConfirmationModal } from "@design-system/Modal/ConfirmationModal";
import { useToaster } from "@design-system/ToastMessages/useToaster";
import { useGoToTemplate } from "@hooks/useGoToTemplate";
import { useScope } from "@state/scopeContext.lib";
import type { ServiceTemplateScheduleCallbacks } from "./scheduleListCells";

interface StatusChange {
    status: ServiceTemplateRecurrenceStatus;
    successMessage: string;
    errorMessage: string;
}

const PAUSE: StatusChange = {
    status: ServiceTemplateRecurrenceStatus.PAUSED,
    successMessage: "Schedule paused",
    errorMessage: "Failed to pause schedule",
};

const RESUME: StatusChange = {
    status: ServiceTemplateRecurrenceStatus.ACTIVE,
    successMessage: "Schedule resumed",
    errorMessage: "Failed to resume schedule",
};

/**
 * A schedule row's actions: Pause and Resume, Go to template, and Delete
 * behind its confirmation. Render `confirmationModal` next to the list.
 */
export const useScheduleRowActions = ({
    onOpenSchedule,
}: Pick<ServiceTemplateScheduleCallbacks, "onOpenSchedule">) => {
    const { organizationId } = useScope();
    const { toast: successToast, error: errorToast } = useToaster();
    const onGoToTemplate = useGoToTemplate();
    const { mutateAsync: deleteSchedule, isPending: isDeleting } =
        useDeleteServiceTemplateScheduleMutation();
    const { mutateAsync: updateStatus, isPending: isUpdatingStatus } =
        useUpdateServiceTemplateScheduleStatusMutation();
    const [deletingScheduleId, setDeletingScheduleId] = useState<string | null>(
        null,
    );

    const changeStatus = async (
        scheduleId: string,
        { status, successMessage, errorMessage }: StatusChange,
    ) => {
        if (!organizationId || isUpdatingStatus) {
            return;
        }
        try {
            await updateStatus({ organizationId, scheduleId, status });
            successToast(successMessage);
        } catch {
            errorToast(errorMessage);
        }
    };

    const confirmDelete = async () => {
        if (!organizationId || !deletingScheduleId || isDeleting) {
            return;
        }
        try {
            await deleteSchedule({
                organizationId,
                scheduleId: deletingScheduleId,
            });
            successToast("Schedule deleted");
            setDeletingScheduleId(null);
        } catch {
            errorToast("Failed to delete schedule");
        }
    };

    const callbacks: ServiceTemplateScheduleCallbacks = {
        onDeleteSchedule: setDeletingScheduleId,
        onPauseSchedule: (scheduleId) => void changeStatus(scheduleId, PAUSE),
        onResumeSchedule: (scheduleId) => void changeStatus(scheduleId, RESUME),
        onGoToTemplate,
        onOpenSchedule,
    };

    const confirmationModal = (
        <ConfirmationModal
            show={deletingScheduleId !== null}
            title="Delete schedule?"
            question="This schedule and its upcoming occurrences will be permanently removed."
            yes="Delete"
            no="Cancel"
            onReject={() => setDeletingScheduleId(null)}
            onConfirm={confirmDelete}
        />
    );

    return { callbacks, isUpdatingStatus, confirmationModal };
};
